import React, { useEffect, useState, useMemo } from 'react';
import { getSummary } from '../../services/analytics.service';

/* ------------------------------------
   Spinner Overlay (Reusable)
------------------------------------ */
const CardLoader = () => (
  <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-10">
    <div className="h-8 w-8 rounded-full border-4 border-gray-200 border-t-blue-500 animate-spin" />
  </div>
);

/* ------------------------------------
   Summary KPIs
------------------------------------ */
const SummaryKPIs = ({ filters }) => {
  const [data, setData] = useState({
    revenue: 0,
    appointments: 0,
    clients: 0,
    abv: 0
  });
  const [loading, setLoading] = useState(false);

  /* ------------------------------------
     Fetch Data
  ------------------------------------ */
  useEffect(() => {
  if (!filters?.from || !filters?.to) return;

  const controller = new AbortController();

  const fetchData = async () => {
    try {
      setLoading(true);

      const summary = await getSummary({
        from: filters.from,
        to: filters.to
      }, controller.signal);
      
      if (controller.signal.aborted) return;

      const revenue = summary?.revenue ?? 0;
      const appointments = summary?.appointments ?? 0;
      const clients = summary?.clients ?? 0;

      setData({
        revenue,
        appointments,
        clients,
        abv: appointments > 0 ? Math.round(revenue / appointments) : 0
      });
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('SummaryKPIs request aborted');
        return;
      }

      console.error('SummaryKPIs load failed', err);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  };

  fetchData();

  return () => {
    controller.abort();
  };
}, [filters?.from, filters?.to]);

  /* ------------------------------------
     Memoized Formatting
  ------------------------------------ */
  const format = (n) => (n || 0).toLocaleString();

  const cards = useMemo(() => [
    {
      title: 'Revenue Generated',
      value: format(data.revenue),
      icon: '💰',
      gradient: 'from-emerald-500 to-green-500'
    },
    {
      title: 'Number of Appointments',
      value: format(data.appointments),
      icon: '📅',
      gradient: 'from-blue-500 to-indigo-500'
    },
    {
      title: 'Number of Clients',
      value: format(data.clients),
      icon: '👥',
      gradient: 'from-purple-500 to-violet-500'
    }
    // {
    //   title: 'Avg Bill Value',
    //   value: format(data.abv),
    //   icon: '🧾',
    //   gradient: 'from-cyan-500 to-sky-500'
    // }
  ], [data]);

  return (
    <div className="grid gap-6 grid-cols-[repeat(auto-fit,minmax(220px,1fr))]">
      {cards.map((card, i) => (
        <div
          key={i}
          className="bg-white rounded-xl shadow-md hover:shadow-xl transition relative"
          style={{ minHeight: 110 }} // ✅ prevents layout jump
        >
          {loading && <CardLoader />}

          <div className={`h-1 rounded-t-xl bg-gradient-to-r ${card.gradient}`} />

          <div className="p-4">
            <div className="flex justify-between mb-2">
              <p className="text-xs font-semibold text-slate-500 uppercase">
                {card.title}
              </p>
              <span className="text-xl">{card.icon}</span>
            </div>

            <p className="text-2xl font-bold text-slate-800">
              {card.value}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default SummaryKPIs;
